import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Desktop from "./Desktop";
import FooterWrapper from "./FooterWrapper";
import FooterLinks from "./FooterLinks";
import { authService } from "../config/api";

const ResetPasswordForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: "",
    });

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError("Invalid reset link");
                setValidating(false);
                return;
            }

            try {
                const response = await authService.validateResetToken(token);
                setTokenValid(response.data.valid);
                if (!response.data.valid) {
                    setError("This reset link is invalid or has expired");
                }
            } catch (err) {
                setError("Failed to validate reset link");
            } finally {
                setValidating(false);
            }
        };

        validateToken();
    }, [token]);

    const handleChange = (e) => {
        setError("");
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (formData.newPassword.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await authService.resetPassword({
                token,
                newPassword: formData.newPassword,
            });

            setSuccess(true);

            // Redirect to signin after 3 seconds
            setTimeout(() => {
                navigate("/signin");
            }, 3000);
        } catch (err) {
            setError(err.message || "Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <div className="w-full relative bg-base-100 overflow-hidden flex flex-col items-center justify-center min-h-screen">
                <span className="loading loading-spinner loading-lg"></span>
                <p className="mt-4 text-base-content/70">Validating reset link...</p>
            </div>
        );
    }

    if (success) {
        return (
            <div className="w-full relative bg-base-100 overflow-hidden flex flex-col items-start justify-start min-h-screen">
                <Desktop />

                <div className="w-full flex items-center justify-center py-12 px-4">
                    <div className="max-w-md w-full space-y-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success/20 mb-4">
                                <svg className="h-8 w-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h1 className="text-3xl font-bold font-heading text-base-content">
                                Password Reset Successful!
                            </h1>
                            <p className="mt-4 text-base text-base-content/70">
                                Your password has been reset successfully. You can now sign in with your new password.
                            </p>
                            <p className="mt-2 text-sm text-base-content/60">
                                Redirecting to sign in page...
                            </p>
                        </div>

                        <div className="mt-8">
                            <Link to="/signin" className="btn btn-primary w-full">
                                Go to Sign In
                            </Link>
                        </div>
                    </div>
                </div>

                <section className="self-stretch flex flex-row items-start justify-start pt-0 px-0 pb-[22px] box-border max-w-full">
                    <div className="flex-1 bg-base-200 flex flex-row items-start justify-start pt-[72px] px-[169px] pb-[71.8px] box-border gap-12 max-w-full lg:flex-wrap mq450:py-[47px] mq450:px-5 mq450:box-border mq750:gap-6 mq750:pl-[84px] mq750:pr-[84px] mq750:box-border">
                        <FooterWrapper />
                        <FooterLinks />
                    </div>
                </section>
                <div className="self-stretch flex flex-row items-start justify-start py-0 pl-[382px] pr-[381px] box-border max-w-full text-base-content/70 mq450:pl-5 mq450:pr-5 mq450:box-border mq750:pl-[191px] mq750:pr-[190px] mq750:box-border">
                    <div className="flex-1 relative leading-[20px] inline-block max-w-full">
                        Copyright @StockAlgoTrader 2024. All Rights Reserved.
                    </div>
                </div>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <div className="w-full relative bg-base-100 overflow-hidden flex flex-col items-start justify-start min-h-screen">
                <Desktop />

                <div className="w-full flex items-center justify-center py-12 px-4">
                    <div className="max-w-md w-full space-y-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-error/20 mb-4">
                                <svg className="h-8 w-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h1 className="text-3xl font-bold font-heading text-base-content">
                                Invalid Reset Link
                            </h1>
                            <p className="mt-4 text-base text-base-content/70">
                                {error || "This password reset link is invalid or has expired."}
                            </p>
                        </div>

                        <div className="space-y-4">
                            <Link to="/forgot-password" className="btn btn-primary w-full">
                                Request New Reset Link
                            </Link>
                            <Link to="/signin" className="btn btn-ghost w-full">
                                Back to Sign In
                            </Link>
                        </div>
                    </div>
                </div>

                <section className="self-stretch flex flex-row items-start justify-start pt-0 px-0 pb-[22px] box-border max-w-full">
                    <div className="flex-1 bg-base-200 flex flex-row items-start justify-start pt-[72px] px-[169px] pb-[71.8px] box-border gap-12 max-w-full lg:flex-wrap mq450:py-[47px] mq450:px-5 mq450:box-border mq750:gap-6 mq750:pl-[84px] mq750:pr-[84px] mq750:box-border">
                        <FooterWrapper />
                        <FooterLinks />
                    </div>
                </section>
                <div className="self-stretch flex flex-row items-start justify-start py-0 pl-[382px] pr-[381px] box-border max-w-full text-base-content/70 mq450:pl-5 mq450:pr-5 mq450:box-border mq750:pl-[191px] mq750:pr-[190px] mq750:box-border">
                    <div className="flex-1 relative leading-[20px] inline-block max-w-full">
                        Copyright @StockAlgoTrader 2024. All Rights Reserved.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full relative bg-base-100 overflow-hidden flex flex-col items-start justify-start min-h-screen">
            <Desktop />

            <div className="w-full flex items-center justify-center py-12 px-4">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold font-heading text-base-content">
                            Reset Your Password
                        </h1>
                        <p className="mt-2 text-base text-base-content/70">
                            Enter your new password below
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        {error && (
                            <div className="alert alert-error">
                                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="fieldset w-full">
                                <label className="label">
                                    <span className="text-sm">New Password</span>
                                </label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    placeholder="Enter new password"
                                    className="input input-bordered bg-base-200 w-full"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                    minLength={8}
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Must be at least 8 characters
                                    </span>
                                </label>
                            </div>

                            <div className="fieldset w-full">
                                <label className="label">
                                    <span className="text-sm">Confirm Password</span>
                                </label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    placeholder="Confirm new password"
                                    className={`input input-bordered bg-base-200 w-full ${formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                                            ? "input-error"
                                            : ""
                                        }`}
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                "Reset Password"
                            )}
                        </button>
                    </form>
                </div>
            </div>

            <section className="self-stretch flex flex-row items-start justify-start pt-0 px-0 pb-[22px] box-border max-w-full">
                <div className="flex-1 bg-base-200 flex flex-row items-start justify-start pt-[72px] px-[169px] pb-[71.8px] box-border gap-12 max-w-full lg:flex-wrap mq450:py-[47px] mq450:px-5 mq450:box-border mq750:gap-6 mq750:pl-[84px] mq750:pr-[84px] mq750:box-border">
                    <FooterWrapper />
                    <FooterLinks />
                </div>
            </section>
            <div className="self-stretch flex flex-row items-start justify-start py-0 pl-[382px] pr-[381px] box-border max-w-full text-base-content/70 mq450:pl-5 mq450:pr-5 mq450:box-border mq750:pl-[191px] mq750:pr-[190px] mq750:box-border">
                <div className="flex-1 relative leading-[20px] inline-block max-w-full">
                    Copyright @StockAlgoTrader 2024. All Rights Reserved.
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordForm;
